import styles from './registration-grid.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import RightsWrapper from '../../../rights-wrapper/rights-wrapper';
import { useState } from 'react';
import { fullNameRenderer } from '../../helpers';
import WebkuaTable from '../../../webkua-table/webkua-table';
import { makeCourseRequest } from '../../../requestUtils';
import { Checkbox, CreateHeaders, ErrorButton, HeaderSizeEnum, Icon, PrimaryButton, SecondaryButton, booleanHeader, dateHeader, textHeader } from '@frontend-workspace/react-shared-lib';
import CreateNewRegistrationDialog from '../create-new-registration-dialog/create-new-registration-dialog';
import EditRegistrationDialog from '../edit-registration-dialog/edit-registration-dialog';
import CancelRegistrationDialog from '../cancel-registration-dialog/cancel-registration-dialog';
import ReactivateRegistrationDialog from '../reactivate-registration-dialog/reactivate-registration-dialog';
import MoveRegistrationDialog from '../move-registration-dialog/move-registration-dialog';
import { IRegistration } from '../../../_models/registration';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface RegistrationGridProps {
  registrations: IRegistration[],
  refreshData: () => void
}

export function RegistrationGrid(props: RegistrationGridProps) {
  const { t } = useTranslation();
  const [isNewRegistrationOpen, setIsNewRegistrationOpen] = useState(false);
  const [editRegistration, setEditRegistration] = useState<IRegistration | undefined>(undefined);
  const [cancelRegistration, setCancelRegistration] = useState<IRegistration | undefined>(undefined);
  const [reactivateRegistration, setReactivateRegistration] = useState<IRegistration | undefined>(undefined);
  const [moveRegistration, setMoveRegistration] = useState<IRegistration | undefined>(undefined);

  const toggleNewRegistrationOpen = () => setIsNewRegistrationOpen(!isNewRegistrationOpen);

  const openEditRegistrationDialog = (e: any) => setEditRegistration(e.data);
  const closeEditRegistrationDialog = () => setEditRegistration(undefined);

  const openCancelRegistrationDialog = (e: any) => setCancelRegistration(e.data);
  const closeCancelRegistrationDialog = () => setCancelRegistration(undefined);

  const openReactivateRegistrationDialog = (e: any) => setReactivateRegistration(e.data);
  const closeReactivateRegistrationDialog = () => setReactivateRegistration(undefined);

  const openMoveRegistrationDialog = (e: any) => setMoveRegistration(e.data);
  const closeMoveRegistrationDialog = () => setMoveRegistration(undefined)

  const editRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="contained"
        size='small'
        onClick={() => openEditRegistrationDialog(params)}>
        {t('standard.change')}
      </PrimaryButton>
    );
  };

  const moveRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="outlined"
        size='small'
        onClick={() => openMoveRegistrationDialog(params)}>
        {t('standard.move')}
      </PrimaryButton>
    );
  };

  const cancelRenderer = (params: ValueFormatterParams) => {
    return (
      !params.data.canceled ?
        <ErrorButton
          variant="outlined"
          size='small'
          onClick={() => openCancelRegistrationDialog(params)}>
          {t('registrationGrid.cancel')}
        </ErrorButton> :
        <SecondaryButton
          variant="outlined"
          size='small'
          onClick={() => openReactivateRegistrationDialog(params)}>
          {t('registrationGrid.reactivate')}
        </SecondaryButton>
    );
  };

  const statusRenderer = (params: ValueFormatterParams) => {
    return !params.data.canceled ? t('registrationGrid.registered') : t('registrationGrid.cancelled');
  };

  const sendEvaluationRenderer = (params: ValueFormatterParams) => {
    const changeEvalutionStatus = () => {
      const body = params.data;
      body.noSurvey = !body.noSurvey;
      makeCourseRequest(`course/registration/updateWithoutEmail`, 'post', body).then(result => {
        props.refreshData();
      });
    }
    return <Checkbox
      checked={!params.value}
      onChange={changeEvalutionStatus}
    />
  };

  return (<>
    <RightsWrapper>
      <div className='mb-3 mb-sm-0'>
        <PrimaryButton
          variant="contained"
          onClick={toggleNewRegistrationOpen}>
          <Icon className="me-1" fontSize="small">add</Icon>{t('registrationGrid.newCourseParticipant')}
        </PrimaryButton>
      </div>
    </RightsWrapper>
    <WebkuaTable
      data={props.registrations}
      headers={CreateHeaders([
        dateHeader("createdAt", t('registrationGrid.enrolled')),
        dateHeader("date", t('standard.lastChanged')),
        textHeader("", t('standard.name'), HeaderSizeEnum.MEDIUM, { renderer: fullNameRenderer }),
        textHeader("person.email", t('standard.email'), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("person.mobile", t('standard.mobileNo'), HeaderSizeEnum.MEDIUM),
        textHeader("", "Status", HeaderSizeEnum.MEDIUM, { renderer: statusRenderer }),
        textHeader("", "", HeaderSizeEnum.MEDIUM, {
          renderer: editRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.MEDIUM, {
          renderer: moveRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.MEDIUM, {
          renderer: cancelRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        booleanHeader("noSurvey", t('registrationGrid.sendEvaluation'), { renderer: sendEvaluationRenderer })
      ])}
    />
    {isNewRegistrationOpen && <CreateNewRegistrationDialog
      onClose={toggleNewRegistrationOpen}
      open={!!isNewRegistrationOpen}
      refreshData={props.refreshData}
    />}
    {editRegistration && <EditRegistrationDialog
      onClose={closeEditRegistrationDialog}
      open={!!editRegistration}
      refreshData={props.refreshData}
      courseId={editRegistration.id}
    />}
    {cancelRegistration && <CancelRegistrationDialog
      onClose={closeCancelRegistrationDialog}
      open={!!cancelRegistration}
      refreshData={props.refreshData}
      courseId={cancelRegistration.id}
    />}
    {reactivateRegistration && <ReactivateRegistrationDialog
      onClose={closeReactivateRegistrationDialog}
      open={!!reactivateRegistration}
      refreshData={props.refreshData}
      courseId={reactivateRegistration.id}
    />}
    {moveRegistration && <MoveRegistrationDialog
      onClose={closeMoveRegistrationDialog}
      open={!!moveRegistration}
      refreshData={props.refreshData}
      course={moveRegistration}
    />}
  </>);
}

export default RegistrationGrid;
